import React from 'react'
import { NavLink, useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { useTheme } from '../context/ThemeContext'

export default function Sidebar({ isOpen, toggleSidebar, isMobile = false, onNavigate, reminderCount = 0, poReminderCount = 0 }) {
  const { user, logout } = useAuth()
  const { isDark } = useTheme()
  const navigate = useNavigate()
  const isExpanded = isMobile ? true : isOpen

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  const operationItems = [
    { path: '/dashboard', label: 'Dashboard', icon: '📊' },
    { path: '/projects', label: 'Projects', icon: '📁' },
  ]

  const documentationItems = [
    { path: '/quotations', label: 'Quotations', icon: '💡' },
    { path: '/purchase-orders', label: 'Purchase Orders', icon: '📋' },
    { path: '/invoices', label: 'Invoices', icon: '📄' },
    { path: '/credit-notes', label: 'Credit Notes', icon: '🧾' },
    { path: '/clients', label: 'Clients', icon: '🏢' },
  ]

  const recordsItems = [
    { path: '/expenses', label: 'Expenses', icon: '💸' },
    { path: '/payroll', label: 'Payroll', icon: '💰' },
    { path: '/employees', label: 'Employees', icon: '👥' },
  ]

  const financialItems = [
    { path: '/journal-entries', label: 'Journal Entries', icon: '📝' },
    { path: '/banking', label: 'Banking', icon: '🏦' },
    { path: '/assets', label: 'Assets', icon: '🏗️' },
    { path: '/liabilities', label: 'Liabilities', icon: '💳' },
  ]

  const appearanceItems = [
    { path: '/settings', label: 'Settings', icon: '⚙️' },
  ]

  const sidebarSurfaceClass = isDark
    ? 'bg-slate-900 text-slate-100 border-slate-700'
    : 'bg-white text-slate-900 border-slate-200'
  const sectionTitleClass = isDark ? 'text-slate-400' : 'text-slate-500'
  const descriptionTextClass = isDark ? 'text-slate-400' : 'text-slate-500'
  const inactiveNavClass = isDark
    ? 'text-slate-300 hover:bg-slate-800 hover:text-white'
    : 'text-slate-700 hover:bg-slate-100 hover:text-slate-900'
  const menuButtonClass = isDark
    ? 'text-slate-300 hover:bg-slate-800 hover:text-white'
    : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
  const dividerClass = isDark ? 'border-slate-700' : 'border-slate-200'

  // Get first letter from company name
  const getInitial = () => {
    return 'P' // PROBARA GIGIH
  }

  const sidebarWidthClass = isMobile
    ? 'fixed inset-y-0 left-0 z-40 w-72 max-w-[85vw] transform transition-transform duration-300'
    : `transition-all duration-300 ${isOpen ? 'w-64' : 'w-18'}`

  const sidebarTransformClass = isMobile
    ? (isOpen ? 'translate-x-0' : '-translate-x-full')
    : ''

  return (
    <div className={`${sidebarSurfaceClass} shadow-lg flex flex-col h-dvh overflow-hidden ${sidebarWidthClass} ${sidebarTransformClass}`}>
      {/* Top Section with Avatar */}
      <div className={`flex items-center justify-center border-b ${dividerClass} ${isExpanded ? 'px-3 py-3' : 'px-0 py-2'}`}>
        {/* Avatar Circle */}
        <div className={`bg-gradient-to-br from-blue-500 to-blue-700 rounded-full flex items-center justify-center flex-shrink-0 ${isExpanded ? 'w-8 h-8' : 'w-9 h-9'}`}>
          <span className={`font-bold text-white ${isExpanded ? 'text-xs' : 'text-sm'}`}>{getInitial()}</span>
        </div>

        {/* Info */}
        {isExpanded && (
          <div className="flex-1 ml-2">
            <h1 className={`text-xs font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>PROBARA GIGIH</h1>
            <p className={`text-xs mt-0.5 ${descriptionTextClass}`}>Operation Management System</p>
          </div>
        )}
      </div>

      {/* User Info (only when expanded) */}
      {/* {isOpen && (
        <div className="px-3 py-2 border-b border-gray-700">
          <p className="text-xs font-semibold text-gray-100">Welcome, {user?.name}</p>
          <p className="text-xs text-gray-500 mt-0.5">Role: {user?.role}</p>
        </div>
      )} */}

      {/* Menu Toggle Button */}
      <div className={`w-full border-b ${dividerClass} ${isExpanded ? 'px-3 py-2' : 'py-3 flex flex-col items-center'}`}>
        <button
          onClick={toggleSidebar}
          className={`flex items-center rounded-md transition w-full ${
            isExpanded ? 'px-2 py-1.5 text-sm' : 'w-9 h-9 justify-center'
          } ${menuButtonClass}`}
          title={isExpanded ? '' : 'Menu'}
        >
          <span className={`flex-shrink-0 ${isExpanded ? 'text-base' : 'text-lg'}`}>{isMobile ? '✕' : '☰'}</span>
          {isExpanded && <span className="ml-2">{isMobile ? 'Close' : 'Menu'}</span>}
        </button>
      </div>

      {/* Navigation */}
      <nav className={`flex-1 overflow-hidden ${isExpanded ? '' : 'flex flex-col items-center'}`}>
        <div className={`h-full overflow-y-auto overflow-x-hidden scrollbar-hidden ${isExpanded ? '' : 'w-full flex flex-col items-center'}`}>
          {/* Operation Section */}
          <div className={`w-full ${isExpanded ? 'px-3 py-2' : 'py-3 flex flex-col items-center'}`}>
          {isExpanded && (
            <h3 className={`text-xs font-semibold uppercase tracking-wider px-2 mb-2 ${sectionTitleClass}`}>
              Operation
            </h3>
          )}
          <div className={`space-y-1 w-full ${isExpanded ? '' : 'flex flex-col items-center space-y-2'}`}>
            {operationItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={() => onNavigate?.()}
                className={({ isActive }) =>
                  `flex items-center rounded-md transition ${
                    isActive
                      ? 'bg-blue-600 text-white'
                      : inactiveNavClass
                  } ${isExpanded ? 'px-2 py-1.5 w-full text-sm' : 'w-9 h-9 justify-center'}`
                }
                title={isExpanded ? '' : item.label}
              >
                <span className={`flex-shrink-0 ${isExpanded ? 'text-base' : 'text-lg'}`}>{item.icon}</span>
                {isExpanded && <span className="ml-2">{item.label}</span>}
              </NavLink>
            ))}
          </div>
          </div>

          {/* Documentations Section */}
          <div className={`w-full border-t ${dividerClass} ${isExpanded ? 'px-3 py-2' : 'py-2 flex flex-col items-center'}`}>
            {isExpanded && (
              <h3 className={`text-xs font-semibold uppercase tracking-wider px-2 mb-2 ${sectionTitleClass}`}>
                Documentations
              </h3>
            )}
            <div className={`space-y-1 w-full ${isExpanded ? '' : 'flex flex-col items-center space-y-2'}`}>
              {documentationItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  onClick={() => onNavigate?.()}
                  className={({ isActive }) =>
                    `relative flex items-center rounded-md transition ${
                      isActive
                        ? 'bg-blue-600 text-white'
                        : inactiveNavClass
                    } ${isExpanded ? 'px-2 py-1.5 w-full text-sm' : 'w-9 h-9 justify-center'}`
                  }
                  title={isExpanded ? '' : item.label}
                >
                  <span className={`flex-shrink-0 ${isExpanded ? 'text-base' : 'text-lg'}`}>{item.icon}</span>
                  {isExpanded && <span className="ml-2">{item.label}</span>}
                  {!isExpanded && item.path === '/purchase-orders' && poReminderCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-600 text-white text-[10px] font-bold rounded-full min-w-[16px] h-4 flex items-center justify-center leading-none px-1">
                      {poReminderCount > 99 ? '99+' : poReminderCount}
                    </span>
                  )}
                  {!isExpanded && item.path === '/invoices' && reminderCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-600 text-white text-[10px] font-bold rounded-full min-w-[16px] h-4 flex items-center justify-center leading-none px-1">
                      {reminderCount > 99 ? '99+' : reminderCount}
                    </span>
                  )}
                  {isExpanded && item.path === '/invoices' && reminderCount > 0 && (
                    <span className="ml-auto bg-red-600 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center leading-none px-1">
                      {reminderCount > 99 ? '99+' : reminderCount}
                    </span>
                  )}
                  {isExpanded && item.path === '/purchase-orders' && poReminderCount > 0 && (
                    <span className="ml-auto bg-red-600 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center leading-none px-1">
                      {poReminderCount > 99 ? '99+' : poReminderCount}
                    </span>
                  )}
                </NavLink>
              ))}
            </div>
          </div>

          {/* Records Section */}
          <div className={`w-full border-t ${dividerClass} ${isExpanded ? 'px-3 py-2' : 'py-2 flex flex-col items-center'}`}>
            {isExpanded && (
              <h3 className={`text-xs font-semibold uppercase tracking-wider px-2 mb-2 ${sectionTitleClass}`}>
                Records
              </h3>
            )}
            <div className={`space-y-1 w-full ${isExpanded ? '' : 'flex flex-col items-center space-y-2'}`}>
              {recordsItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  onClick={() => onNavigate?.()}
                  className={({ isActive }) =>
                    `flex items-center rounded-md transition ${
                      isActive
                        ? 'bg-blue-600 text-white'
                        : inactiveNavClass
                    } ${isExpanded ? 'px-2 py-1.5 w-full text-sm' : 'w-9 h-9 justify-center'}`
                  }
                  title={isExpanded ? '' : item.label}
                >
                  <span className={`flex-shrink-0 ${isExpanded ? 'text-base' : 'text-lg'}`}>{item.icon}</span>
                  {isExpanded && <span className="ml-2">{item.label}</span>}
                </NavLink>
              ))}
            </div>
          </div>

          {/* Financial Section */}
          <div className={`w-full border-t ${dividerClass} ${isExpanded ? 'px-3 py-2' : 'py-2 flex flex-col items-center'}`}>
            {isExpanded && (
              <h3 className={`text-xs font-semibold uppercase tracking-wider px-2 mb-2 ${sectionTitleClass}`}>
                Financial
              </h3>
            )}
            <div className={`space-y-1 w-full ${isExpanded ? '' : 'flex flex-col items-center space-y-2'}`}>
              {financialItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  onClick={() => onNavigate?.()}
                  className={({ isActive }) =>
                    `flex items-center rounded-md transition ${
                      isActive
                        ? 'bg-blue-600 text-white'
                        : inactiveNavClass
                    } ${isExpanded ? 'px-2 py-1.5 w-full text-sm' : 'w-9 h-9 justify-center'}`
                  }
                  title={isExpanded ? '' : item.label}
                >
                  <span className={`flex-shrink-0 ${isExpanded ? 'text-base' : 'text-lg'}`}>{item.icon}</span>
                  {isExpanded && <span className="ml-2">{item.label}</span>}
                </NavLink>
              ))}
            </div>
          </div>

          <div className={`w-full border-t ${dividerClass} ${isExpanded ? 'px-3 py-2' : 'py-3 flex flex-col items-center'}`}>
            {isExpanded && (
              <h3 className={`text-xs font-semibold uppercase tracking-wider px-2 mb-2 ${sectionTitleClass}`}>
                Appearances
              </h3>
            )}
            <div className={`space-y-1 w-full ${isExpanded ? '' : 'flex flex-col items-center space-y-2'}`}>
              {appearanceItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  onClick={() => onNavigate?.()}
                  className={({ isActive }) =>
                    `flex items-center rounded-md transition ${
                      isActive
                        ? 'bg-blue-600 text-white'
                        : inactiveNavClass
                    } ${isExpanded ? 'px-2 py-1.5 w-full text-sm' : 'w-9 h-9 justify-center'}`
                  }
                  title={isExpanded ? '' : item.label}
                >
                  <span className={`flex-shrink-0 ${isExpanded ? 'text-base' : 'text-lg'}`}>{item.icon}</span>
                  {isExpanded && <span className="ml-2">{item.label}</span>}
                </NavLink>
              ))}
            </div>
          </div>
        </div>
      </nav>

      {/* Logout Button at Bottom */}
      <div className={`border-t ${dividerClass} ${isExpanded ? 'p-3' : 'p-2 flex justify-center'}`}>
        <button
          onClick={handleLogout}
          className={`bg-red-600 hover:bg-red-700 text-white rounded-md transition font-medium flex items-center justify-center ${
            isExpanded ? 'w-full px-2 py-1.5 text-sm' : 'w-9 h-9 text-lg'
          }`}
          title={isExpanded ? '' : 'Logout'}
        >
          🚪
          {isExpanded && <span className="ml-2">Logout</span>}
        </button>
      </div>
    </div>
  )
}
